import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, BookOpen, Search, Shield } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

const ORG_COLORS = {
  'Commission Européenne': '#4285F4',
  'France Compétences': '#0055A4',
  'Certif Pro': '#E63946',
  'GIP Pix': '#7C3AED',
  'France Travail (Pôle Emploi)': '#1D4ED8',
  'BCS/ITSMF': '#0891B2',
  'Agence Nationale de Lutte contre l\'Illettrisme': '#059669',
  'Ministère du Travail': '#B45309',
};

function ReferentialCard({ ref: r }) {
  const [expanded, setExpanded] = useState(false);
  const color = ORG_COLORS[r.organization] || '#ff6b6b';
  const hasDomains = r.domains && r.domains.length > 0;

  return (
    <div className="kurso-card overflow-hidden" data-testid={`ref-card-${r.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-white font-bold text-base">{r.name}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[#a0a0ab]">v{r.version}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[#a0a0ab]">{r.date}</span>
            </div>
            <p className="text-xs font-medium" style={{ color }}>{r.organization}</p>
          </div>
          {r.official_url && (
            <a href={r.official_url} target="_blank" rel="noopener noreferrer"
              className="flex-shrink-0 p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
              title="Site officiel">
              <ExternalLink size={14} className="text-[#a0a0ab] group-hover:text-white transition-colors" />
            </a>
          )}
        </div>
        <p className="text-[#a0a0ab] text-sm leading-relaxed">{r.description}</p>

        {hasDomains && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 flex items-center gap-1.5 text-xs text-[#ff6b6b] hover:text-[#ff5555] transition-colors font-medium"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? 'Masquer les domaines' : `Voir les ${r.domains.length} domaines`}
          </button>
        )}
      </div>

      {hasDomains && expanded && (
        <div className="border-t border-white/5 px-5 pb-5 pt-3">
          <div className="space-y-3">
            {r.domains.map(domain => (
              <div key={domain.id}>
                <p className="text-white text-xs font-semibold mb-1.5">
                  <span style={{ color }} className="mr-1">{domain.id}.</span> {domain.name}
                </p>
                {domain.competencies && domain.competencies.length > 0 && (
                  <ul className="space-y-0.5 ml-3">
                    {domain.competencies.map(c => (
                      <li key={c.id} className="text-[#a0a0ab] text-xs flex items-start gap-1.5">
                        <span className="text-white/30 flex-shrink-0 mt-0.5">▸</span>
                        <span><span className="text-white/50">{c.id}</span> — {c.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Referentials() {
  const [referentials, setReferentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/referentials`);
        setReferentials(res.data.referentials || []);
      } catch {
        setReferentials([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = referentials.filter(r =>
    !search || r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.organization.toLowerCase().includes(search.toLowerCase()) ||
    r.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      <Helmet>
        <title>Référentiels de compétences — Kurso</title>
        <meta name="description" content="Bibliothèque des référentiels de compétences : DigComp 2.2, RNCP, CléA, PIX, ROME 4.0, SFIA 9, ANLCI, Qualiopi." />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#ff6b6b]/20 flex items-center justify-center">
              <BookOpen size={18} className="text-[#ff6b6b]" />
            </div>
            <h1 className="text-4xl font-heading font-bold text-white">Référentiels</h1>
          </div>
          <p className="text-[#a0a0ab] max-w-2xl">
            Bibliothèque des cadres de référence officiels en matière de compétences numériques, professionnelles et de formation. Utilisés pour aligner vos cours et certifications.
          </p>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-2 bg-[#4285F4]/10 border border-[#4285F4]/20 rounded-xl p-3 mb-6 text-xs text-[#a0c4ff]">
          <Shield size={14} className="flex-shrink-0 mt-0.5 text-[#4285F4]" />
          <span>Ces référentiels sont les sources officielles. Les contenus Kurso peuvent être alignés sur ces cadres pour faciliter les certifications et financements OPCO.</span>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a0a0ab]" />
          <input
            type="text" placeholder="Rechercher un référentiel (DigComp, RNCP, CléA...)"
            value={search} onChange={e => setSearch(e.target.value)}
            data-testid="referentials-search"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#ff6b6b]/50"
          />
        </div>

        {/* Results count */}
        <p className="text-xs text-[#a0a0ab] mb-4">
          {loading ? 'Chargement...' : `${filtered.length} référentiel${filtered.length !== 1 ? 's' : ''}`}
        </p>

        {/* Grid */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="kurso-card p-5 h-24 animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen size={36} className="text-[#a0a0ab] mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">Aucun résultat</p>
            <p className="text-[#a0a0ab] text-sm">Essayez un autre terme de recherche</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(r => <ReferentialCard key={r.id} ref={r} />)}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 kurso-card p-6 text-center border-[#ff6b6b]/20">
          <h3 className="text-white font-bold mb-2">Aligner vos cours sur un référentiel</h3>
          <p className="text-[#a0a0ab] text-sm mb-4">Associez vos formations aux compétences officielles pour faciliter les financements OPCO et la certification Qualiopi.</p>
          <a href="/dashboard" className="inline-block bg-[#ff6b6b] text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#ff5555] transition-colors">
            Accéder à mes cours →
          </a>
        </div>
      </div>
    </Layout>
  );
}
